import React from 'react';
import { NavLink } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';
import { Separator } from '../ui/Separator';

const Logo = () => (
  <div className="flex items-center gap-3 px-6 py-5 border-b border-border">
    <div className="h-10 w-10 bg-grn-gold rounded-lg flex items-center justify-center shadow-sm">
      <div className="h-8 w-8 bg-grn-navy rounded-md flex items-center justify-center">
        <span className="text-grn-gold font-bold text-xs">GRN</span>
      </div>
    </div>
    <div>
      <h1 className="text-foreground font-bold text-lg leading-tight">GovTrack</h1>
      <p className="text-muted-foreground text-xs font-medium tracking-wider">NAMIBIA</p>
    </div>
  </div>
);

const NavItem = ({ to, icon, label, count }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      clsx(
        'flex items-center gap-3 px-3 mx-3 py-2 text-sm font-medium transition-all rounded-md',
        isActive
          ? 'bg-accent text-accent-foreground'
          : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
      )
    }
  >
    <SafeIcon icon={icon} className="text-lg shrink-0" />
    <span className="flex-1 truncate">{label}</span>
    {count && (
      <span className="px-2 py-0.5 rounded-md bg-destructive text-destructive-foreground text-xs font-bold">
        {count}
      </span>
    )}
  </NavLink>
);

const NavSection = ({ title, children }) => (
  <div className="py-2">
    <h3 className="px-6 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
      {title}
    </h3>
    <div className="space-y-1">
      {children}
    </div>
  </div>
);

export const Sidebar = () => {
  const { sidebarOpen, data } = useApp();
  const alertCount = data.alerts.length;

  if (!sidebarOpen) return null;

  return (
    <aside className="w-64 bg-card h-screen fixed left-0 top-0 z-50 flex flex-col border-r border-border">
      <Logo />
      
      <nav className="flex-1 py-4 overflow-y-auto">
        <NavSection title="Strategic Command">
          <NavItem to="/" icon={FiIcons.FiGrid} label="Dashboard" />
          <NavItem to="/system-importance" icon={FiIcons.FiAward} label="Strategic Importance" />
          <NavItem to="/system-architecture" icon={FiIcons.FiCpu} label="System Architecture" />
          <NavItem to="/monthly-performance" icon={FiIcons.FiBarChart} label="Monthly Performance" />
          <NavItem to="/alerts" icon={FiIcons.FiAlertTriangle} label="Alerts & Escalations" count={alertCount} />
          <NavItem to="/compliance" icon={FiIcons.FiClock} label="Compliance Deadlines" />
          <NavItem to="/oma-mandate" icon={FiIcons.FiCheckSquare} label="OMA Mandate Monitor" />
          <NavItem to="/cabinet-decisions" icon={FiIcons.FiBookOpen} label="Cabinet Decisions" count={data.cabinetStats?.overdueDecisions} />
          <NavItem to="/ministry-compliance" icon={FiIcons.FiCheckCircle} label="Ministry Compliance" />
        </NavSection>

        <Separator className="my-4" />

        <NavSection title="Modules">
          <NavItem to="/projects" icon={FiIcons.FiBriefcase} label="Projects" />
          <NavItem to="/project-calendar" icon={FiIcons.FiCalendar} label="Project Calendar" />
          <NavItem to="/procurement" icon={FiIcons.FiFileText} label="Procurement" />
          <NavItem to="/ndp6" icon={FiIcons.FiTarget} label="NDP6 Progress" />
          <NavItem to="/reports" icon={FiIcons.FiBarChart2} label="Reports & Audit" />
        </NavSection>

        <Separator className="my-4" />

        <NavSection title="Configuration">
          <NavItem to="/users" icon={FiIcons.FiUsers} label="User Management" />
          <NavItem to="/settings" icon={FiIcons.FiSettings} label="System Settings" />
        </NavSection>
      </nav>

      <div className="p-4 border-t border-border bg-muted/30">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-md bg-grn-gold flex items-center justify-center text-grn-navy font-bold text-sm">
            PM
          </div>
          <div className="overflow-hidden flex-1">
            <p className="text-sm font-medium text-foreground truncate">Prime Minister's Office</p>
            <p className="text-xs text-muted-foreground">Super Admin</p>
          </div>
        </div>
      </div>
    </aside>
  );
};